import {
  Badge,
  DataTable,
  FlexBox,
  Icon,
  IconButton,
  Page,
  Text,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import type { EventLogT, EventPayloadT } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AppLink } from 'components/appLink';
import { getEventLog, getSubscription } from 'components/settings/api/webhooks';

import { parseEventPayload } from '../../webhooks.utils';
import { DeliveryStatus } from '../deliveryStatus';
import { PayloadModal } from '../payloadModal';

export function EventLog(): JSX.Element {
  const [logs, setLogs] = useState<EventLogT[]>([]);
  const [payload, setPayload] = useState<EventPayloadT | null>(null);
  const [subscriptionName, setSusbcriptionName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const { id, event } = useParams<{ id?: string; event?: string }>();

  useEffect(() => {
    getAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAll = useCallback(() => {
    if (id && event) {
      setIsLoading(true);
      setIsError(false);

      getEventLog({ id, event })
        .then(({ data }) => {
          setLogs(data.events);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          setIsError(true);
        });

      getSubscription(id).then(({ data }) => setSusbcriptionName(data.name));
    }
  }, [id, event]);

  return (
    <>
      <Page.Section
        titleMeta={
          <FlexBox gap="200" alignItems="center">
            {!id && (
              <Text.Heading variant="heading-3">
                {subscriptionName}
              </Text.Heading>
            )}

            {id && (
              <AppLink
                path="/settings/integrations/webhooks/:id"
                params={{ path: { id } }}
              >
                <Text.Heading variant="heading-3">
                  {subscriptionName}
                </Text.Heading>
              </AppLink>
            )}

            <Icon name="angle-right" />

            <Text.Heading variant="heading-3">{event}</Text.Heading>

            {logs?.length > 0 && (
              <Badge size="small" variant="secondary">
                {logs.length}
              </Badge>
            )}

            <Tooltip content="Help and documentation.">
              <IconButton
                icon="question"
                variant="tertiary"
                onClick={() =>
                  window.open(
                    'https://docs.dixa.io/docs/webhooks-guide',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              />
            </Tooltip>
          </FlexBox>
        }
      >
        <DataTable<EventLogT, never>
          data={logs}
          tableStyleProps={{
            stickyHeader: true,
          }}
          tableStatusProps={{
            loading: isLoading,
            loadingFailed: isError,
            onLoadingRetry: () => getAll(),
            emptyState:
              !logs.length && !isError
                ? {
                    title: 'Nothing to show here',
                    description: 'No events found',
                  }
                : undefined,
          }}
          columns={[
            {
              label: 'Event Id',
              selector: ({ payload }) => {
                const parsedPayload = parseEventPayload(payload);
                return parsedPayload?.event_id || '';
              },
            },
            {
              label: 'Status',
              selector: ({
                delivery_status: { response_code, response_text, is_success },
              }) => (
                <DeliveryStatus
                  key="status"
                  code={response_code}
                  text={response_text}
                  isSuccess={is_success}
                />
              ),
            },
            {
              label: 'Timestamp',
              selector: ({ delivery_status: { delivery_timestamp } }) => (
                <Timestamp key="timestamp" time={delivery_timestamp} />
              ),
            },
            {
              label: undefined,
              style: {
                width: 0,
              },
              selector: ({ payload }) => (
                <Tooltip content="View payload">
                  <IconButton
                    variant="tertiary"
                    icon="code"
                    onClick={() => {
                      const parsedPayload = parseEventPayload(payload);
                      if (parsedPayload) {
                        setPayload(parsedPayload);
                      }
                    }}
                  />
                </Tooltip>
              ),
            },
          ]}
        />
      </Page.Section>

      {payload && (
        <PayloadModal
          isOpen={!!payload}
          payload={payload}
          onClose={() => {
            setPayload(null);
          }}
        />
      )}
    </>
  );
}
